/** Separate model and durable-chat context controls for the input bar. */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarPickersMessages } from "@/i18n/messages/input-bar-pickers";
import type { InputBarConversationController } from "./input-bar-types";
import {
  formatModelLabel,
  type EffortChoice,
  type RuntimeKey,
} from "@/lib/model-registry";
import { ModelPickerPopover } from "./model-picker-popover";
import { InputBarConversationContext } from "./input-bar-conversation-context";
import { useInputPopoverPlacement } from "./use-input-popover-placement";
import { useInputBarChatAgent } from "@/hooks/use-denker-composer-agent";
import type {
  InputEffortPickOptions,
  InputModelPickOptions,
} from "@/hooks/use-input-model-actions";
import { providerRuntimeId } from "@/lib/denker-composer-agent";

export interface InputBarFocusedAgent {
  id: string;
  adapterType: RuntimeKey;
  runtimeId?: string;
  model: string;
  effort: EffortChoice;
  contextPercent: number | null;
  /** Saved usage for a selected durable chat before live tokens are available. */
  persistedContextPercent?: number | null;
  persistedContextUsedTokens?: number | null;
  persistedContextWindowMax?: number | null;
  conversationId?: string;
  agentName?: string;
  spaceId?: string;
  runtimeSessionId?: string;
}

/** A CLI runtime tab in the model picker. `detected` is false when the
 *  companion didn't find the binary on PATH — the tab is still rendered
 *  (so users can discover it) but disabled with an "install" hint
 *  instead of a "log in" hint. */
export interface InputBarProvider {
  type: RuntimeKey;
  runtimeId?: string;
  /** True = authenticated, false = not authenticated, null = unknown (no check available). */
  loggedIn: boolean | null;
  /** Whether the companion daemon found the CLI binary on PATH.
   *  Defaults to true so existing call sites keep working. */
  detected?: boolean;
  /** Terminal command the user runs to (re-)log in. */
  loginCommand?: string;
  /** True for Denker-managed bundled runtimes. */
  managed?: boolean;
  source?: string;
}

interface Props {
  focusedAgent: InputBarFocusedAgent | null;
  providers: InputBarProvider[];
  onUpdateModel: (
    agentId: string,
    provider: RuntimeKey,
    runtimeId: string,
    model: string,
    options?: InputModelPickOptions,
  ) => Promise<void>;
  onUpdateEffort: (
    agentId: string,
    effort: EffortChoice,
    options?: InputEffortPickOptions,
  ) => Promise<void>;
  conversationController?: InputBarConversationController;
  className?: string;
  density?: "default" | "standalone";
  workerUsage?: { used: number; max: number } | null;
  onOpenChange?: (open: boolean) => void;
  /** Tray popovers must sit above the tray and remain interactive outside its bounds. */
  popoverScope?: "tray";
}

export function InputBarModelPill({
  focusedAgent: templateAgent,
  providers,
  onUpdateModel,
  onUpdateEffort,
  conversationController,
  className,
  density = "default",
  workerUsage,
  onOpenChange,
  popoverScope,
}: Props) {
  const focusedAgent = useInputBarChatAgent(
    templateAgent,
    providers,
    !conversationController,
  );
  const t = useT(inputBarPickersMessages);
  const [modelOpen, setModelOpen] = useState(false);
  const standalone = density === "standalone";
  const [contextOpen, setContextOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const getPopoverAnchor = useCallback(
    () =>
      (buttonRef.current?.closest("form") as HTMLElement | null) ??
      buttonRef.current,
    [],
  );
  const popoverStyle = useInputPopoverPlacement(popoverRef, {
    active: modelOpen,
    align: "end",
    gap: 14,
    getAnchor: getPopoverAnchor,
    strategy: "fixed",
  });
  const handleContextOpenChange = useCallback(
    (nextOpen: boolean) => {
      setContextOpen(nextOpen);
      onOpenChange?.(nextOpen);
      if (nextOpen) setModelOpen(false);
    },
    [onOpenChange],
  );

  useEffect(() => () => onOpenChange?.(false), [onOpenChange]);

  if (!focusedAgent) return null;

  const fullLabel = formatModelLabel(
    focusedAgent.adapterType,
    focusedAgent.model,
  );
  const displayLabel = standalone
    ? fullLabel.replace(/^Claude\s+/i, "")
    : fullLabel;

  return (
    <div className={cn("relative flex items-center", className)}>
      <button
        ref={buttonRef}
        type="button"
        title={fullLabel}
        onClick={(e) => {
          e.stopPropagation();
          const nextOpen = !modelOpen;
          setModelOpen(nextOpen);
          onOpenChange?.(nextOpen);
          if (nextOpen) setContextOpen(false);
        }}
        className={cn(
          "flex items-center rounded-full text-secondary transition-colors hover:text-primary",
          standalone
            ? "h-6 gap-1 px-1.5 text-appkit-caption"
            : "h-7 gap-1.5 px-2 text-appkit-small",
        )}
        data-testid={
          standalone ? "standalone-bar-model" : "input-bar-model-pill"
        }
        aria-label={t("model", { label: fullLabel })}
        aria-haspopup="menu"
        aria-expanded={modelOpen}
      >
        <span
          className={cn(
            "max-w-[110px] truncate whitespace-nowrap",
            standalone && "max-w-[100px]",
          )}
        >
          {displayLabel}
        </span>
        <Icons.ChevronDown className="h-3 w-3 shrink-0" />
      </button>
      <InputBarConversationContext
        focusedAgent={focusedAgent}
        open={contextOpen}
        onOpenChange={handleContextOpenChange}
        conversationController={conversationController}
        popoverScope={popoverScope}
        density={density}
        workerUsage={workerUsage}
      />
      {modelOpen &&
        createPortal(
          <ModelPickerPopover
            ref={popoverRef}
            style={popoverStyle}
            providers={providers}
            currentProvider={focusedAgent.adapterType}
            currentRuntimeId={
              focusedAgent.runtimeId ?? focusedAgent.adapterType
            }
            currentModel={focusedAgent.model}
            currentEffort={focusedAgent.effort}
            onChangeModel={(provider, model) => {
              // Keep the picker open so model and thinking can be selected together.
              return onUpdateModel(
                focusedAgent.id,
                provider.type,
                providerRuntimeId(provider),
                model,
                conversationController ? undefined : { inputBarChat: true },
              );
            }}
            onChangeEffort={(effort) => {
              if (conversationController) {
                return onUpdateEffort(focusedAgent.id, effort);
              }
              // The effort belongs to the shown chat model, so the template
              // (and the new chat it starts) takes that model in the same save.
              const showsChatRuntime =
                !!templateAgent &&
                (focusedAgent.model !== templateAgent.model ||
                  focusedAgent.runtimeId !== templateAgent.runtimeId);
              return onUpdateEffort(focusedAgent.id, effort, {
                inputBarChat: true,
                ...(showsChatRuntime
                  ? {
                      runtime: {
                        provider: focusedAgent.adapterType,
                        runtimeId:
                          focusedAgent.runtimeId ?? focusedAgent.adapterType,
                        model: focusedAgent.model,
                      },
                    }
                  : {}),
              });
            }}
            onClose={() => {
              setModelOpen(false);
              onOpenChange?.(false);
            }}
            className={cn(
              popoverScope === "tray" ? undefined : "z-[60]",
              "max-w-full",
            )}
            popoverScope={popoverScope}
            stopEscapePropagation={standalone}
            motionState="open"
          />,
          document.body,
        )}
    </div>
  );
}
